import { useEffect, useState, type ReactNode } from "react";
import {
  BookOpenText,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  PackageCheck,
  FilePlus2,
  FileText,
  FolderOpen,
  Home,
  Landmark,
  ChartNoAxesCombined,
  LifeBuoy,
  Layers3,
  LogOut,
  MapPinned,
  Menu,
  Printer,
  Scissors,
  ShieldCheck,
  UsersRound,
  X,
} from "lucide-react";
import ExchangeBadge from "../platform/ExchangeBadge";
import UserAvatar from './UserAvatar';
import { apiRequest } from "../platform/api";
import type { SessionUser } from "../platform/types";
import { canOpenSection, isProductionOnly } from "../../shared/printRoles";

export type SystemDestination =
  | "home"
  | "digital"
  | "vinyl"
  | "offset"
  | "quotes"
  | "customers"
  | "products"
  | "characteristics"
  | "guide"
  | "orders"
  | "stations"
  | "deliveries"
  | "support"
  | "finance"
  | "bi"
  | "cleanup"
  | "storage"
  | "files"
  | "admin";

type IconType = typeof Home;
type NavItem = { id: SystemDestination; label: string; icon: IconType };

const NAV_GROUPS: Array<{ id: string; title: string; items: NavItem[] }> = [
  {
    id: "workspace",
    title: "Espacio de trabajo",
    items: [
      { id: "home", label: "Inicio", icon: Home },
      { id: "quotes", label: "Cotizaciones", icon: FileText },
      { id: "customers", label: "Clientes", icon: UsersRound },
      { id: "orders", label: "Órdenes de trabajo", icon: ClipboardList },
      { id: "deliveries", label: "Notas de entrega", icon: PackageCheck },
      { id: "support", label: "Reportar bug o soporte", icon: LifeBuoy },
    ],
  },
  {
    id: "calculators",
    title: "Calculadoras",
    items: [
      { id: "digital", label: "Impresión digital", icon: Printer },
      { id: "vinyl", label: "Vinil y plóter", icon: Scissors },
      { id: "offset", label: "Impresión Offset", icon: Layers3 },
    ],
  },
  {
    id: "management",
    title: "Gestión",
    items: [
      { id: "products", label: "Productos", icon: Layers3 },
      { id: "characteristics", label: "Características", icon: ClipboardList },
      { id: 'files', label: 'Explorador de archivos', icon: FolderOpen },
      { id: "stations", label: "Imprenta y estaciones", icon: MapPinned },
      { id: "finance", label: "Finanzas", icon: Landmark },
      { id: "bi", label: "Business Intelligence", icon: ChartNoAxesCombined },
      { id: "admin", label: "Administración", icon: ShieldCheck },
      { id: "cleanup", label: "Limpieza de registros", icon: ShieldCheck },
      { id: 'storage', label: 'Archivos y almacenamiento', icon: FolderOpen },
    ],
  },
];

const TITLES: Record<SystemDestination, string> = {
  home: "Inicio",
  digital: "Impresión digital",
  vinyl: "Vinil y plóter",
  offset: "Impresión Offset",
  quotes: "Cotizaciones",
  customers: "Clientes",
  products: "Productos",
  characteristics: "Características de producción",
  guide: "Manual",
  orders: "Órdenes de trabajo",
  stations: "Imprenta y estaciones",
  deliveries: "Notas de entrega",
  support: "Reportar bug o soporte directo",
  finance: "Finanzas",
  bi: "Business Intelligence",
  admin: "Administración",
  cleanup: "Developer · Limpieza de registros",
  storage: 'Archivos y almacenamiento',
  files: 'Explorador de archivos',
};

function savedCollapsed() {
  try {
    return localStorage.getItem("carioprint_sidebar_collapsed") === "true";
  } catch {
    return false;
  }
}

export default function SystemShell({
  active,
  user,
  basketCount,
  onNavigate,
  onCreateQuote,
  onOpenGuide,
  onLogout,
  showExchange = true,
  children,
}: {
  active: SystemDestination;
  user: SessionUser;
  basketCount: number;
  onNavigate: (destination: SystemDestination) => void;
  onCreateQuote: () => void;
  onOpenGuide?: () => void;
  onLogout: () => void;
  showExchange?: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(savedCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [financeNotifications, setFinanceNotifications] = useState(0);
  const [openGroups, setOpenGroups] = useState<string[]>(["workspace"]);
  const narrow = collapsed && !mobileOpen;

  useEffect(() => {
    const activeGroup = NAV_GROUPS.find((group) => group.items.some((item) => item.id === active));
    if (activeGroup) {
      setOpenGroups((current) => current.includes(activeGroup.id) ? current : [...current, activeGroup.id]);
    }
  }, [active]);

  useEffect(() => {
    if (!user.financeAccess) return;
    let active = true;
    const refresh = () => apiRequest<{ total: number }>("/api/finance/notification-count")
      .then((response) => { if (active) setFinanceNotifications(response.total); })
      .catch(() => undefined);
    void refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user.financeAccess]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      try {
        localStorage.setItem("carioprint_sidebar_collapsed", String(!current));
      } catch {
        // La navegación sigue funcionando si el almacenamiento está bloqueado.
      }
      return !current;
    });
  };

  const navigate = (destination: SystemDestination) => {
    onNavigate(destination);
    setMobileOpen(false);
  };

  const availableGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canOpenSection(user, item.id)).filter((item) =>
      item.id === "finance"
        ? user.financeAccess
        : item.id === "admin"
          ? user.role === "admin"
          : item.id === "cleanup" ? user.role === "developer" : item.id === 'storage' ? ['admin','developer'].includes(user.role) : true,
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="min-h-screen bg-[#f5f6fb] text-slate-900">
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Cerrar menú"
          onClick={() => setMobileOpen(false)}
          className="no-print fixed inset-0 z-[65] bg-[#100f2e]/60 backdrop-blur-[2px] lg:hidden"
        />
      ) : null}

      <aside
        aria-label="Menú principal"
        className={`no-print fixed inset-y-3 left-3 z-[70] flex flex-col rounded-[26px] border border-white/10 bg-[#17133c] text-white shadow-[18px_18px_50px_rgba(20,18,58,0.16)] transition-[width,transform] duration-300 ease-out ${narrow ? "w-[82px]" : "w-[272px]"} ${mobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+24px)] lg:translate-x-0"}`}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[25px]">
          <div className={`relative border-b border-white/10 ${narrow ? "px-4 py-4" : "px-4 py-5"}`}>
            <div className="flex min-w-0 items-center gap-3 pr-8 lg:pr-0">
              <UserAvatar name={user.name} photo={user.avatarData} />
              {!narrow ? <><span className="min-w-0 flex-1"><strong className="block truncate text-[12px] font-black">{user.name}</strong><span className="block text-[10px] font-semibold text-white/50">{user.role === "admin" ? "Administrador" : user.role === "developer" ? "Developer · soporte" : "Usuario"}</span></span><button type="button" onClick={onLogout} aria-label="Cerrar sesión" title="Cerrar sesión" className="hidden rounded-lg p-1.5 text-white/55 hover:bg-white/10 hover:text-white lg:inline-flex"><LogOut className="h-4 w-4" /></button></> : null}
            </div>
            <button type="button" onClick={() => setMobileOpen(false)} aria-label="Cerrar menú" className="absolute right-3 top-1 rounded-lg p-1.5 text-white/65 hover:bg-white/10 lg:hidden"><X className="h-4 w-4" /></button>
          </div>

        <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 py-5" aria-label="Secciones del sistema">
          <button type="button" onClick={() => navigate("guide")} aria-label="Manual" title="Manual" aria-current={active === "guide" ? "page" : undefined} className={`flex w-full items-center rounded-xl py-3 text-left transition-colors ${narrow ? "justify-center px-2" : "gap-3 px-3"} ${active === "guide" ? "bg-gradient-to-r from-[#a42970] to-[#713080] text-white shadow-lg" : "border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"}`}>
            <BookOpenText aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
            {!narrow ? <span className="text-[12px] font-bold">Manual</span> : null}
          </button>
          {availableGroups.map((group) => {
            const groupOpen = openGroups.includes(group.id);
            return <div key={group.id}>
              {!narrow ? (
                <button type="button" aria-expanded={groupOpen} onClick={() => setOpenGroups((current) => groupOpen ? current.filter((id) => id !== group.id) : [...current, group.id])} className="mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[10px] font-black uppercase tracking-[0.16em] text-white/45 hover:bg-white/5 hover:text-white/75">
                  {group.title}<ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${groupOpen ? "" : "-rotate-90"}`} />
                </button>
              ) : <div className="mb-2 h-px bg-white/10" />}
              <div className={`grid transition-[grid-template-rows] duration-300 ${groupOpen || narrow ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}><div className="min-h-0 overflow-hidden space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const selected = active === item.id;
                  return <button key={item.id} type="button" onClick={() => navigate(item.id)} title={narrow ? item.label : undefined} aria-current={selected ? "page" : undefined} className={`group flex w-full items-center rounded-xl py-2.5 text-left transition-all duration-200 ${narrow ? "justify-center px-2" : "gap-3 px-3"} ${selected ? "bg-gradient-to-r from-[#a42970] to-[#713080] text-white shadow-lg shadow-black/15" : "text-white/65 hover:bg-white/10 hover:text-white"}`}>
                    <Icon aria-hidden="true" className={`h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110 ${selected ? "text-white" : "text-white/60 group-hover:text-white"}`} />
                    {!narrow ? <span className="min-w-0 flex-1 truncate text-[12px] font-bold">{item.label}</span> : null}
                    {!narrow && item.id === "quotes" && basketCount > 0 ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-white/20 px-1 text-[9px] font-black">{basketCount}</span> : null}
                    {!narrow && item.id === "finance" && financeNotifications > 0 ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-black">{financeNotifications > 99 ? "99+" : financeNotifications}</span> : null}
                  </button>;
                })}
              </div></div>
            </div>;
          })}
        </nav>

          <div className="border-t border-white/10 px-3 py-3">
            {narrow || mobileOpen ? <button type="button" onClick={onLogout} aria-label="Cerrar sesión" title="Cerrar sesión" className="mx-auto grid h-8 w-8 place-items-center rounded-lg text-white/55 hover:bg-white/10 hover:text-white"><LogOut className="h-4 w-4" /></button> : null}
            <div className={`text-[9px] font-black text-white/50 ${narrow ? "mt-2 border-t border-white/10 pt-2 text-center" : "px-2"}`} title="Versión instalada">{narrow ? "v" : "Versión instalada v"}{typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev"}</div>
          </div>
        </div>

        <button type="button" onClick={toggleCollapsed} aria-label={collapsed ? "Expandir menú" : "Contraer menú"} title={collapsed ? "Expandir menú" : "Contraer menú"} className="absolute -right-4 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-[#8c1d5d] text-white shadow-xl shadow-[#231f60]/25 transition-[transform,background] duration-300 hover:scale-110 hover:bg-[#a72b74] lg:flex"><ChevronLeft className={`h-4 w-4 transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`} /></button>
      </aside>

      <div className={`min-w-0 transition-[padding-left] duration-300 ease-out ${collapsed ? "lg:pl-[110px]" : "lg:pl-[300px]"}`}>
        <header className="no-print sticky top-0 z-[55] flex min-h-[66px] items-center justify-between gap-3 border-b border-[#e5e6ef] bg-white/90 px-4 backdrop-blur-xl sm:px-7">
          <div className="flex min-w-0 items-center gap-3"><button type="button" onClick={() => setMobileOpen(true)} aria-label="Abrir menú" className="rounded-xl border border-slate-200 p-2 text-[#231f60] lg:hidden"><Menu className="h-5 w-5" /></button><div className="min-w-0"><p className="text-[9px] font-black uppercase tracking-[0.18em] text-[#8c1d5d]">CarioPrint / {active === "home" ? "Panel" : "Trabajo"}</p><h1 className="truncate text-sm font-black text-[#231f60] sm:text-base">{TITLES[active]}</h1></div></div>
          <div className="flex items-center gap-2">{showExchange && active === "finance" && user.financeAccess ? <div className="hidden sm:block"><ExchangeBadge /></div> : null}{onOpenGuide ? <button type="button" onClick={onOpenGuide} aria-label="Abrir instrucciones" title="Instrucciones" className="rounded-xl border border-slate-200 p-2.5 text-[#231f60] hover:border-[#8c1d5d]/30 hover:bg-[#faf0f6]"><BookOpenText className="h-4 w-4" /></button> : null}{!isProductionOnly(user) ? <button type="button" onClick={onCreateQuote} className="inline-flex items-center gap-2 rounded-xl bg-[#231f60] px-3 py-2.5 text-[11px] font-black text-white shadow-sm transition hover:bg-[#40358b]"><FilePlus2 className="h-4 w-4" /><span className="hidden sm:inline">Nueva cotización</span>{basketCount > 0 ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#b42a78] px-1 text-[9px]">{basketCount}</span> : null}</button> : null}</div>
        </header>
        <main id="main-content" className="min-h-[calc(100vh-66px)]">{children}</main>
      </div>
    </div>
  );
}
